"use client";

import { c as _c } from "react/compiler-runtime";
import type { MouseEvent } from "react";
import { LOCATION_HREF, STATIC_LABEL, useNearestOffice } from "@/app/components/SmileStickyHeader/useNearestOffice";
import { useSmileChromeRoute } from "@/app/components/SmileStickyHeader/SmileChromeRoute";
import { megaPathOf, resolveSmileMegaPanel, toggleSmileMegaPanel, useSmileMegaMenu } from "@/app/components/SmileStickyHeader/smileMegaMenuStore";
import { SMILE_NAV_FLYOUT_ENABLED } from "@/lib/constants";
import { isSameOriginRelativeHref, resolveSmileBookHref, type SmileBookOfficePin } from "@/lib/utils/smile-chrome/smile-book-href";
import { trackSmileChromeLink } from "@/lib/utils/smile-chrome/smile-tracking";
import type { BffFacility } from "@/lib/__generated/beffe-sdk";
import { SMILE_HEADER_LOGO_ALT_DEFAULT, SMILE_HEADER_LOGO_HREF_DEFAULT, SMILE_HEADER_LOGO_URL_DEFAULT, SMILE_HEADER_MOBILE_LOGO_URL_DEFAULT, SMILE_HEADER_NAV_ITEMS_DEFAULT, SMILE_HEADER_PORTAL_HREF_DEFAULT, SMILE_HEADER_PORTAL_LABEL_DEFAULT } from "./smileHeaderContent";
import { toSmileLinkItems, type SmileLinkEntryInput } from "@/lib/utils/smile-chrome/link-entries";
import { EditorFeedback } from "@/app/components/studio/EditorFeedback/EditorFeedback";
import { CalendarGlyph, HamburgerGlyph, PersonGlyph as BarPersonGlyph } from "@/app/components/SmileStickyHeader/SmileCompactChromeGlyphs";
import { isCompactChromeNavHref, selectCompactChromeQuickLinks, toSmileLogoHref } from "@/lib/utils/smile-chrome/compact-chrome";
import compact from "@/app/components/SmileStickyHeader/SmileCompactChrome.module.css";
import styles from "./SmileHeader.module.css";
import { SMILE_HEADER_PERSON_GLYPH_PATH, SMILE_HEADER_PHONE_GLYPH_PATH, SMILE_HEADER_PIN_DOT, SMILE_HEADER_PIN_GLYPH_PATH } from "./smileHeaderGlyphs";
export interface SmileHeaderProps {
  /**
   * Lockup on the utility bar: a Brandfolder asset URL. Defaults to the
   * official white lockup; an explicitly empty value falls back to the alt
   * text, never a swapped or drawn logo.
   */
  logoUrl?: string;
  /**
   * Lockup the compact chrome below 600px shows on its white bar and in the
   * drawer. This atom never renders it; it stamps the value on its root as
   * data-smile-mobile-logo, which is where SmileMobileHeader reads it.
   * Defaults to the official navy lockup from Brandfolder (D23).
   */
  mobileLogoUrl?: string;
  logoAlt?: string;
  logoHref?: string;
  /**
   * The nav trigger row. Studio hands over the entries of a bound Navigation
   * Group's Links field (Navigation Link or Page entries in CDA shape); plain
   * {label, href} rows are accepted too. Unbound, empty, or nothing usable =
   * SMILE_HEADER_NAV_ITEMS_DEFAULT. Mega-panel content stays authored beside
   * this atom; the atom only owns the trigger row.
   */
  navItems?: ReadonlyArray<SmileLinkEntryInput>;
  locatorLabel?: string;
  /**
   * Locator destination used until the geo ladder resolves a nearest office.
   * Defaults to OFFICE_LOCATOR_HREF_DEFAULT ("/dentist/") per Eric Zapfe's
   * 9/04 QA. Must be a rooted, relative path — see toLocatorHref below.
   */
  locatorHref?: string;
  contactPhoneDisplay?: string;
  contactPhoneNumber?: string;
  /** Book Online CTA label. Measured off the authored band, which says exactly "Book Online". */
  bookLabel?: string;
  /**
   * Book Online CTA destination. EMPTY (the default, and what the SDK hands
   * over for a variable the node never authored) means "the page decides":
   * resolveSmileBookHref lands on TAG's scheduler with the page's reason path
   * on every page (/schedule-an-appointment/emergency/ on the emergency LP
   * since Eric Zapfe's 2026-09-10 directive), from the route and slug
   * SmileChromeRouteProvider carries (D1/D9); only the standalone
   * /smile/schedule/?variant=emergency route still resolves its own dispatcher.
   * An authored value must be a rooted, same-origin relative path; an
   * absolute, protocol-relative or control-character href is refused and the
   * page decides instead (TAG-163, see lib/utils/smile-chrome/smile-book-href).
   * Deliberately no destructuring default below: `= "/schedule-an-appointment/"`
   * would turn every unauthored instance into an authored generic one.
   */
  bookHref?: string;
  /**
   * Whether Book Online carries the office the header CHIP shows (fc= on
   * TAG's scheduler, facilityCode= on the emergency dispatcher). "chip", the
   * default since Chris Mason's 2026-09-10 ruling (the header Book CTA carries
   * the pinned office like the page's Book CTAs), reads the same `office` the
   * chip renders - the nearest office the geo ladder resolved, else the
   * app-context selected office painted first - so the two can never
   * disagree, and the href follows the chip when the ladder resolves after
   * hydration (React re-renders this anchor in place; the compact chrome
   * observes it). "none" opts an instance out. A literal code is content,
   * authored in bookHref, and an authored pin is never overwritten.
   */
  bookOfficePin?: SmileBookOfficePin;
  /** Drops the Book Online CTA entirely for the rare instance that shouldn't carry one. */
  hideBookCta?: boolean;
  /**
   * The patient-portal log-in link (the client's 2026-10-14 ask: "portal log
   * in to desktop header and mobile hamburger"): its label and destination.
   * Rendered at the end of the utility row, after the office chip, as a
   * plain anchor marked data-smile-portal, which is how the compact chrome's
   * drawer sources it for its own row (SmileMobileHeader classifies the
   * atom's anchors by role, and this marker keeps the link out of the
   * category list). An empty label removes the link from both surfaces. The
   * href must be a rooted same-origin path (toPortalHref): the portal lives
   * at /patient-services/ on every door, and an absolute URL here would
   * bounce a staging visitor to production (TAG-163), so anything else falls
   * back to the default.
   */
  portalLabel?: string;
  portalHref?: string;
  /**
   * The "Menu opens on the office card" variable: the compact chrome's
   * drawer opens on the visitor's office card, then the categories. On by
   * default since 2026-10-16, when the client asked for the QA'd a01a test
   * arm to go live; an author turns it off per placement. This atom renders
   * no drawer itself; it stamps the flag on its root as
   * data-smile-drawer-office-card, which is where SmileMobileHeader reads it
   * (the same contract as the data-smile-mobile-logo attributes).
   */
  bbDrawerOfficeCard?: boolean;
  /**
   * The mega-menu kill switch, mirrored from SMILE_NAV_FLYOUT_ENABLED
   * (lib/constants.ts) so this atom attaches the authored panels under
   * exactly the condition the band does. A prop for the tests only, never a
   * Studio variable: flipping the flyout is a code decision (Chris + Nathan,
   * 2026-08-31), not a per-instance one.
   */
  flyoutEnabled?: boolean;
  /**
   * v2: at-rest transparency over a hero image (mirrors the underlap+scrim
   * grammar SmileStickyHeaderEnhancer.module.css already owns). Wins over
   * bandStyle when both are authored.
   */
  transparentOverHero?: boolean;
  /** v2: solid-band treatment when transparentOverHero is off. */
  bandStyle?: "cobalt" | "white";
  /** v2: identifies this instance in analytics payloads + data-analytics-id. */
  analyticsId?: string;
  /**
   * SDK-injected editor flag (see SmileHeaderWithContextDefault). Keeps the
   * geo ladder inert on the Studio canvas so the editor never fires
   * geolocation or the IP-fix fetch.
   */
  isEditorMode?: boolean;
  /**
   * First-paint fallback resolved server-side (app-context selectedOffice),
   * forwarded by SmileHeaderWithContextDefault. Overridden the moment the
   * native ladder resolves its own fix. `code` is the facility code, so a
   * Book pin follows the chip from the first paint.
   */
  initialOffice?: {
    label: string;
    href: string;
    code?: string;
  } | null;
  /**
   * The office the visitor picked (useChosenOffice), forwarded by
   * SmileHeaderWithContextDefault. Outranks the geo ladder for the chip and
   * the Book pin, so the header never books a different office than the
   * page's Book CTAs after a pick (Eric Zapfe, TAG, 2026-10-20). Null until
   * a pick is on record.
   */
  chosenOffice?: {
    label: string;
    href: string;
    code?: string;
  } | null;
  /**
   * The visitor's SELECTED office (app-context selectedOffice), forwarded by
   * SmileHeaderWithContextDefault. TAG's header grammar stamps every
   * navigation/contact event with website_selected_facility_id/name; the
   * LinkWrapper this atom used to wrap its anchors in supplied them from the
   * same context, so a plain <a> has to carry them explicitly or the atom's
   * events silently lose the office (verifier finding, 2026-09-06).
   */
  analyticsOffice?: {
    code: string;
    name?: string | null;
  } | null;
  /** SDK-injected className carrying the builtInStyles (cfMargin/cfVisibility/cfBackgroundColor/cfTextColor). */
  className?: string;
}

/**
 * Default locator destination — Eric Zapfe (TAG) QA, 2026-09-04. His header
 * table pins "Find An Office" at https://www.aspendental.com/dentist, which is
 * the production office locator and therefore sits DELIBERATELY OUTSIDE the
 * /smile sandbox. That is an intentional exception to the "relative /smile
 * paths only" rule that governs this atom's Book CTA: the office locator is an
 * Aspen-owned page BB does not host under /smile, and Eric asked for it by
 * name, so pointing at a /smile stand-in would be wrong.
 *
 * RELATIVE, not the absolute URL Eric wrote, and the difference is load-
 * bearing. This atom renders its anchors from code, so normalize-slot-hrefs —
 * which only rewrites hrefs arriving through Contentful slot data — never sees
 * this value and will not strip an origin off it. A literal
 * https://www.aspendental.com/dentist would therefore ship as-is and bounce a
 * wwwstg visitor onto production mid-journey, the TAG-163 defect exactly. The
 * path exists in both environments (verified 2026-09-04: www 200, wwwstg 200
 * on /dentist/; the slash-less /dentist 308-redirects on both, hence the
 * trailing slash here), so the relative form reaches Eric's page on whichever
 * door the visitor is already on. Same shape as the resolved-office hrefs the
 * geo ladder already emits ("/dentist/pa/homestead/…/"), so the fallback and
 * the upgrade now live in one namespace instead of two.
 */
export const OFFICE_LOCATOR_HREF_DEFAULT = "/dentist/";

/**
 * Keeps an AUTHORED locator href inside the current environment: a rooted,
 * single-slash, non-traversing path passes (the locator lives at /dentist/,
 * outside /smile, by design); an absolute URL, a protocol-relative //host/… ,
 * or anything that climbs with ".." falls back to OFFICE_LOCATOR_HREF_DEFAULT.
 * Same rule as the Book CTA guard (lib/utils/smile-chrome/smile-book-href),
 * for the same reason: nothing downstream strips an origin for this atom, so
 * refusing beats rendering a cross-environment jump.
 */
function toLocatorHref(href: string | undefined): string {
  const candidate = href?.trim();
  if (!candidate) return OFFICE_LOCATOR_HREF_DEFAULT;
  return isSameOriginRelativeHref(candidate) ? candidate : OFFICE_LOCATOR_HREF_DEFAULT;
}

/**
 * Keeps an AUTHORED portal href inside the current environment, the locator's
 * rule exactly: a rooted, single-slash, non-traversing path passes; an absolute
 * URL, a protocol-relative one or a traversal falls back to
 * SMILE_HEADER_PORTAL_HREF_DEFAULT (/patient-services/, which exists on every
 * door). Refusing beats rendering a cross-environment jump.
 */
function toPortalHref(href: string | undefined): string {
  const candidate = href?.trim();
  if (!candidate) return SMILE_HEADER_PORTAL_HREF_DEFAULT;
  return isSameOriginRelativeHref(candidate) ? candidate : SMILE_HEADER_PORTAL_HREF_DEFAULT;
}

/**
 * The band's leading calendar glyph, inlined.
 *
 * The authored band ships this as an <img> of a WHITE Contentful SVG
 * (icon-calendar.svg, 18x20) and then recolors it on scroll with a
 * `filter: brightness(0) … hue-rotate(203deg) …` stack, because an <img> can
 * never inherit currentColor. Inlining the same path and filling it with
 * currentColor reaches the identical two-tone result — white over the hero,
 * navy once the band flips — with the ink flip already handled by the same
 * colour rules that drive the nav triggers, and no filter arithmetic and no
 * network request for chrome that must paint on first frame. The path data is
 * copied verbatim from that asset.
 */
/**
 * The band's phone glyph, inlined. Measured off the authored band at 1440: the
 * nav-row phone anchor reads "phone1 (833) ..." because the band renders a
 * Material Icons OUTLINED ligature there. We inline the same 24-unit outlined
 * "phone" path (smileHeaderGlyphs.ts) so the atom never depends on the icon
 * font being loaded and never drifts to the filled variant again (D9 D-3).
 *
 * 24x24, the Material icon's own box. The band's glyph measured 24x24 on
 * 2026-09-07 (tagcf compare, computed styles); the 20 this used to be read
 * as a smaller, lighter phone beside text that was itself 2px too small.
 */
function PhoneIcon() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d={SMILE_HEADER_PHONE_GLYPH_PATH} /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/**
 * The band's location pin, inlined: Material Icons OUTLINED "location_on",
 * the ring plus its separate dot (smileHeaderGlyphs.ts), so the utility row's
 * geo chip matches the band's outlined pin without a cross-entry asset URL.
 *
 * Boxed 13x16 like the band's pin <img> (measured 2026-09-07 at 1440). The
 * viewBox is cropped to the glyph's own bounds (x 5-19, y 2-22 of the 24-unit
 * grid) so the pin itself stands 16px tall the way the band's does, rather
 * than a 16px box holding a 13px pin. Still currentColor, so the utility
 * row's ink rules recolour it with the label.
 */
function LocationPinIcon() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg width="13" height="16" viewBox="5 2 14 20" fill="currentColor" aria-hidden="true"><path d={SMILE_HEADER_PIN_GLYPH_PATH} /><circle cx={SMILE_HEADER_PIN_DOT.cx} cy={SMILE_HEADER_PIN_DOT.cy} r={SMILE_HEADER_PIN_DOT.r} /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/**
 * The portal link's person glyph: Material Icons OUTLINED "person"
 * (smileHeaderGlyphs.ts), the family the pin and the handset come from, so the
 * utility row's three affordances read as one set. 20x20 on the 24-unit grid,
 * currentColor so the row's ink rules recolour it with the label.
 */
function PersonIcon() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d={SMILE_HEADER_PERSON_GLYPH_PATH} /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function BookCalendarIcon() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg className={styles.bookIcon} width="18" height="20" viewBox="0 0 18 20" fill="none" aria-hidden="true" focusable="false"><path d="M2 20C1.45 20 0.979167 19.8042 0.5875 19.4125C0.195833 19.0208 0 18.55 0 18V4C0 3.45 0.195833 2.97917 0.5875 2.5875C0.979167 2.19583 1.45 2 2 2H3V1C3 0.716667 3.09583 0.479167 3.2875 0.2875C3.47917 0.0958333 3.71667 0 4 0C4.28333 0 4.52083 0.0958333 4.7125 0.2875C4.90417 0.479167 5 0.716667 5 1V2H13V1C13 0.716667 13.0958 0.479167 13.2875 0.2875C13.4792 0.0958333 13.7167 0 14 0C14.2833 0 14.5208 0.0958333 14.7125 0.2875C14.9042 0.479167 15 0.716667 15 1V2H16C16.55 2 17.0208 2.19583 17.4125 2.5875C17.8042 2.97917 18 3.45 18 4V18C18 18.55 17.8042 19.0208 17.4125 19.4125C17.0208 19.8042 16.55 20 16 20H2ZM2 18H16V8H2V18ZM2 6H16V4H2V6Z" fill="currentColor" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/** Builds the office-page href for a bare facility slug, mirroring useNearestOffice's officeHref helper. */
function toOfficeHref(office: {
  href?: string;
} | BffFacility | null | undefined): string | null {
  if (!office || typeof office !== "object") return null;
  const href = (office as {
    href?: string;
  }).href;
  return typeof href === "string" && href.length > 0 ? href : null;
}

/**
 * Base presentational component for the /smile chrome header. Renders the
 * faithful two-row band (utility strip + nav row) with the logo, the
 * authored nav trigger labels, and a NATIVELY-resolved location link —
 * `useNearestOffice() ?? initialOffice`, guarded by isEditorMode so the geo
 * ladder never fires on the Studio canvas. Preserves the data-smile-* marker
 * contract so the colocated SmileStickyHeaderEnhancer scroll/scrim CSS and
 * SmileMobileHeader's MutationObserver keep working against this atom too.
 */
export function SmileHeader(t0) {
  const $ = _c(84);
  const {
    logoUrl: t1,
    mobileLogoUrl: t2,
    logoAlt: t3,
    logoHref: t4,
    navItems,
    locatorLabel: t5,
    locatorHref: t6,
    contactPhoneDisplay: t7,
    contactPhoneNumber: t8,
    bookLabel: t9,
    bookHref,
    bookOfficePin: t10,
    hideBookCta: t11,
    portalLabel: t12,
    portalHref: t13,
    bbDrawerOfficeCard: t14,
    transparentOverHero: t15,
    bandStyle: t16,
    analyticsId: t17,
    isEditorMode: t18,
    initialOffice: t19,
    chosenOffice: t20,
    analyticsOffice: t21,
    flyoutEnabled: t22,
    className
  } = t0;
  const logoUrl = t1 === undefined ? SMILE_HEADER_LOGO_URL_DEFAULT : t1;
  const mobileLogoUrl = t2 === undefined ? SMILE_HEADER_MOBILE_LOGO_URL_DEFAULT : t2;
  const logoAlt = t3 === undefined ? SMILE_HEADER_LOGO_ALT_DEFAULT : t3;
  const logoHref = t4 === undefined ? SMILE_HEADER_LOGO_HREF_DEFAULT : t4;
  const locatorLabel = t5 === undefined ? STATIC_LABEL : t5;
  const locatorHref = t6 === undefined ? OFFICE_LOCATOR_HREF_DEFAULT : t6;
  const contactPhoneDisplay = t7 === undefined ? "1 (800) 277-3633" : t7;
  const contactPhoneNumber = t8 === undefined ? "+18002773633" : t8;
  const bookLabel = t9 === undefined ? "Book Online" : t9;
  const bookOfficePin = t10 === undefined ? "chip" : t10;
  const hideBookCta = t11 === undefined ? false : t11;
  const portalLabel = t12 === undefined ? SMILE_HEADER_PORTAL_LABEL_DEFAULT : t12;
  const portalHref = t13 === undefined ? SMILE_HEADER_PORTAL_HREF_DEFAULT : t13;
  const bbDrawerOfficeCard = t14 === undefined ? true : t14;
  const transparentOverHero = t15 === undefined ? false : t15;
  const bandStyle = t16 === undefined ? "cobalt" : t16;
  const analyticsId = t17 === undefined ? "smile_header" : t17;
  const isEditorMode = t18 === undefined ? false : t18;
  const initialOffice = t19 === undefined ? null : t19;
  const chosenOffice = t20 === undefined ? null : t20;
  const analyticsOffice = t21 === undefined ? null : t21;
  const flyoutEnabled = t22 === undefined ? SMILE_NAV_FLYOUT_ENABLED : t22;
  const navBinding = toSmileLinkItems(navItems);
  const navRow = navBinding?.items.length ? navBinding.items : SMILE_HEADER_NAV_ITEMS_DEFAULT;
  const navSource = navBinding?.items.length ? "entry" : "default";
  const t23 = !isEditorMode;
  let t24;
  if ($[0] !== t23) {
    t24 = {
      enabled: t23
    };
    $[0] = t23;
    $[1] = t24;
  } else {
    t24 = $[1];
  }
  const resolved = useNearestOffice(t24);
  const office = isEditorMode ? null : chosenOffice ?? resolved ?? initialOffice;
  let t25;
  if ($[2] !== locatorHref) {
    t25 = toLocatorHref(locatorHref);
    $[2] = locatorHref;
    $[3] = t25;
  } else {
    t25 = $[3];
  }
  const locatorDestination = t25;
  let t26;
  if ($[4] !== office) {
    t26 = toOfficeHref(office);
    $[4] = office;
    $[5] = t26;
  } else {
    t26 = $[5];
  }
  const resolvedOfficeHref = t26;
  const officeHref = resolvedOfficeHref && resolvedOfficeHref !== LOCATION_HREF ? resolvedOfficeHref : locatorDestination;
  const officeLabel = office?.label ?? locatorLabel;
  let t27;
  if ($[6] !== portalLabel) {
    t27 = portalLabel?.trim();
    $[6] = portalLabel;
    $[7] = t27;
  } else {
    t27 = $[7];
  }
  const portalText = t27;
  const showPortalLink = Boolean(portalText);
  let t28;
  if ($[8] !== portalHref) {
    t28 = toPortalHref(portalHref);
    $[8] = portalHref;
    $[9] = t28;
  } else {
    t28 = $[9];
  }
  const portalDestination = t28;
  const contactPhoneHref = `tel:${contactPhoneNumber}`;
  let t29;
  if ($[10] !== bookLabel) {
    t29 = bookLabel?.trim();
    $[10] = bookLabel;
    $[11] = t29;
  } else {
    t29 = $[11];
  }
  const bookText = t29;
  const chromeRoute = useSmileChromeRoute();
  let t30;
  if ($[12] !== bookHref || $[13] !== bookOfficePin || $[14] !== chromeRoute.route || $[15] !== chromeRoute.slugPath || $[16] !== isEditorMode || $[17] !== office?.code) {
    t30 = isEditorMode ? resolveSmileBookHref({
      authored: bookHref,
      route: "external",
      slugPath: null
    }) : resolveSmileBookHref({
      authored: bookHref,
      route: chromeRoute.route,
      slugPath: chromeRoute.slugPath,
      officeCode: office?.code,
      officePin: bookOfficePin
    });
    $[12] = bookHref;
    $[13] = bookOfficePin;
    $[14] = chromeRoute.route;
    $[15] = chromeRoute.slugPath;
    $[16] = isEditorMode;
    $[17] = office?.code;
    $[18] = t30;
  } else {
    t30 = $[18];
  }
  const book = t30;
  const bookDestination = book.href;
  const mega = useSmileMegaMenu();
  const megaPanels = flyoutEnabled && !isEditorMode ? mega.panels : null;
  let t31;
  if ($[19] !== mega) {
    t31 = (event, key) => {
      const opening = mega.activeKey !== key;
      event.currentTarget.setAttribute("aria-expanded", String(opening));
      toggleSmileMegaPanel(key);
    };
    $[19] = mega;
    $[20] = t31;
  } else {
    t31 = $[20];
  }
  const onMegaToggle = t31;
  const showBookCta = !hideBookCta && Boolean(bookText);
  const compactQuickLinks = selectCompactChromeQuickLinks(navRow.filter(_temp));
  let t32;
  if ($[21] !== logoHref) {
    t32 = toSmileLogoHref(logoHref);
    $[21] = logoHref;
    $[22] = t32;
  } else {
    t32 = $[22];
  }
  const compactLogoHref = t32;
  let t33;
  if ($[23] !== mobileLogoUrl) {
    t33 = mobileLogoUrl?.trim() || SMILE_HEADER_MOBILE_LOGO_URL_DEFAULT;
    $[23] = mobileLogoUrl;
    $[24] = t33;
  } else {
    t33 = $[24];
  }
  const compactLogoSrc = t33;
  let t34;
  if ($[25] !== logoAlt) {
    t34 = logoAlt?.trim() || SMILE_HEADER_LOGO_ALT_DEFAULT;
    $[25] = logoAlt;
    $[26] = t34;
  } else {
    t34 = $[26];
  }
  const compactLogoAlt = t34;
  let t35;
  if ($[27] !== analyticsOffice?.code || $[28] !== analyticsOffice?.name || $[29] !== isEditorMode) {
    t35 = (event_0, linkText) => {
      if (isEditorMode) {
        return;
      }
      trackSmileChromeLink({
        href: event_0.currentTarget.getAttribute("href") ?? "",
        linkText,
        location: "header",
        facilityCode: analyticsOffice?.code,
        facilityName: analyticsOffice?.name ?? undefined
      });
    };
    $[27] = analyticsOffice?.code;
    $[28] = analyticsOffice?.name;
    $[29] = isEditorMode;
    $[30] = t35;
  } else {
    t35 = $[30];
  }
  const track = t35;
  const t36 = transparentOverHero ? styles.transparentOverHero : "";
  const t37 = bandStyle === "white" ? styles.bandWhite : "";
  let t38;
  if ($[31] !== className || $[32] !== t36 || $[33] !== t37) {
    t38 = [styles.root, t36, t37, className].filter(Boolean);
    $[31] = className;
    $[32] = t36;
    $[33] = t37;
    $[34] = t38;
  } else {
    t38 = $[34];
  }
  const rootClassName = t38.join(" ");
  let t39;
  if ($[35] !== logoAlt || $[36] !== track) {
    t39 = event_1 => track(event_1, logoAlt);
    $[35] = logoAlt;
    $[36] = track;
    $[37] = t39;
  } else {
    t39 = $[37];
  }
  let t40;
  if ($[38] !== logoAlt || $[39] !== logoUrl) {
    t40 = logoUrl ? <img className={styles.logo} src={logoUrl} alt={logoAlt} /> : <span className={styles.logo}>{logoAlt}</span>;
    $[38] = logoAlt;
    $[39] = logoUrl;
    $[40] = t40;
  } else {
    t40 = $[40];
  }
  let t41;
  if ($[41] !== logoHref || $[42] !== t39 || $[43] !== t40) {
    t41 = <a href={logoHref} className={styles.logoLink} onClick={t39}>{t40}</a>;
    $[41] = logoHref;
    $[42] = t39;
    $[43] = t40;
    $[44] = t41;
  } else {
    t41 = $[44];
  }
  let t42;
  if ($[45] !== officeLabel || $[46] !== track) {
    t42 = event_2 => track(event_2, officeLabel);
    $[45] = officeLabel;
    $[46] = track;
    $[47] = t42;
  } else {
    t42 = $[47];
  }
  let t43;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <LocationPinIcon />;
    $[48] = t43;
  } else {
    t43 = $[48];
  }
  let t44;
  if ($[49] !== officeLabel) {
    t44 = <span className={styles.locationLabel} key={officeLabel}>{officeLabel}</span>;
    $[49] = officeLabel;
    $[50] = t44;
  } else {
    t44 = $[50];
  }
  let t45;
  if ($[51] !== officeHref || $[52] !== t42 || $[53] !== t44) {
    t45 = <a className={styles.locationLink} href={officeHref} data-smile-location="" data-testid="smile_header_location" onClick={t42}>{t43}{t44}</a>;
    $[51] = officeHref;
    $[52] = t42;
    $[53] = t44;
    $[54] = t45;
  } else {
    t45 = $[54];
  }
  let t46;
  if ($[55] !== portalDestination || $[56] !== portalText || $[57] !== showPortalLink || $[58] !== track) {
    t46 = showPortalLink ? <a className={styles.portalLink} href={portalDestination} data-smile-portal="" data-testid="smile_header_portal" onClick={event_3 => track(event_3, portalText ?? "")}><PersonIcon /><span>{portalText}</span></a> : null;
    $[55] = portalDestination;
    $[56] = portalText;
    $[57] = showPortalLink;
    $[58] = track;
    $[59] = t46;
  } else {
    t46 = $[59];
  }
  let t47;
  if ($[60] !== t45 || $[61] !== t46) {
    t47 = <div className={styles.utilityLinks}>{t45}{t46}</div>;
    $[60] = t45;
    $[61] = t46;
    $[62] = t47;
  } else {
    t47 = $[62];
  }
  let t48;
  if ($[63] !== t41 || $[64] !== t47) {
    t48 = <div className={styles.utilityRow} data-smile-row="utility">{t41}{t47}</div>;
    $[63] = t41;
    $[64] = t47;
    $[65] = t48;
  } else {
    t48 = $[65];
  }
  let t49;
  if ($[66] !== mega || $[67] !== megaPanels || $[68] !== onMegaToggle || $[69] !== track) {
    t49 = (item_0, index) => {
      const panel = megaPanels ? resolveSmileMegaPanel(megaPanels, {
        pathname: megaPathOf(item_0.href),
        label: item_0.label
      }) : null;
      const open = panel !== null && mega.activeKey === panel.key;
      return <li key={`${item_0.href}-${index}`}>{panel ? <><a href={item_0.href} className={styles.navTrigger} data-smile-mega-source-hidden="" aria-hidden="true" tabIndex={-1}>{item_0.label}</a><button type="button" className={`${styles.navTrigger} ${styles.navTriggerButton}`} data-smile-mega-trigger={open ? "open" : "closed"} data-smile-mega-key={panel.key} aria-expanded={open} aria-controls={panel.id} onClick={event_4 => onMegaToggle(event_4, panel.key)}>{item_0.label}</button></> : <a href={item_0.href} className={styles.navTrigger} target={item_0.openInNewTab ? "_blank" : undefined} rel={item_0.openInNewTab ? "noopener noreferrer" : undefined} onClick={event_5 => track(event_5, item_0.label)}>{item_0.label}</a>}</li>;
    };
    $[66] = mega;
    $[67] = megaPanels;
    $[68] = onMegaToggle;
    $[69] = track;
    $[70] = t49;
  } else {
    t49 = $[70];
  }
  let t50;
  if ($[71] !== contactPhoneDisplay || $[72] !== track) {
    t50 = event_6 => track(event_6, contactPhoneDisplay);
    $[71] = contactPhoneDisplay;
    $[72] = track;
    $[73] = t50;
  } else {
    t50 = $[73];
  }
  let t51;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <PhoneIcon />;
    $[74] = t51;
  } else {
    t51 = $[74];
  }
  let t52;
  if ($[75] !== contactPhoneDisplay || $[76] !== contactPhoneHref || $[77] !== t50) {
    t52 = <a className={`${styles.contactPhone} InvocaNumber`} href={contactPhoneHref} data-analytics="call_click" onClick={t50} suppressHydrationWarning={true}>{t51}{contactPhoneDisplay}</a>;
    $[75] = contactPhoneDisplay;
    $[76] = contactPhoneHref;
    $[77] = t50;
    $[78] = t52;
  } else {
    t52 = $[78];
  }
  let t53;
  if ($[79] !== bookDestination || $[80] !== bookText || $[81] !== showBookCta || $[82] !== track) {
    t53 = showBookCta ? <a href={bookDestination} className={styles.bookCta} onClick={event_7 => track(event_7, bookText ?? "")}><BookCalendarIcon />{bookText}</a> : null;
    $[79] = bookDestination;
    $[80] = bookText;
    $[81] = showBookCta;
    $[82] = track;
    $[83] = t53;
  } else {
    t53 = $[83];
  }
  return <header className={rootClassName} data-smile-header-atom="" data-smile-section="" data-analytics-id={analyticsId} data-smile-mobile-logo={mobileLogoUrl} data-smile-logo-alt={logoAlt} data-smile-logo-href={logoHref} data-smile-book-source={book.source} data-smile-booking-route={chromeRoute.route} data-smile-drawer-office-card={bbDrawerOfficeCard ? "" : undefined}>{t48}<div className={styles.navRow} data-smile-row="nav"><ul className={styles.navList} data-smile-nav-source={navSource}>{navRow.map(t49)}</ul>{t52}{t53}</div>{isEditorMode ? null : <div data-smile-compact-chrome="" className={compact.chrome}><div className={compact.bar}><div className={compact.barLeft}><button type="button" className={compact.barIcon} aria-expanded={false} aria-label="Open menu"><HamburgerGlyph /></button></div><a className={compact.barLogo} href={compactLogoHref} onClick={event_8 => track(event_8, compactLogoAlt)}><img className={compact.barLogoImg} src={compactLogoSrc} alt={compactLogoAlt} /></a><div className={compact.barRight}>{showPortalLink ? <a className={compact.barPortal} href={portalDestination} aria-label={portalText} data-smile-portal-bar="" data-testid="smile_header_portal_bar" onClick={event_9 => track(event_9, portalText ?? "")}><BarPersonGlyph /></a> : null}{showBookCta ? <a className={compact.barBook} href={bookDestination} aria-label="Book" onClick={event_10 => track(event_10, "Book")}><CalendarGlyph fill="#fff" /><span>Book</span></a> : null}</div></div>{compactQuickLinks.length > 0 ? <nav className={compact.navRow} aria-label="Primary navigation">{compactQuickLinks.map(item_1 => <a key={item_1.href} href={item_1.href} onClick={event_11 => track(event_11, item_1.label)}>{item_1.label}</a>)}</nav> : null}</div>}{isEditorMode && navBinding && navBinding.dropped.length > 0 ? <EditorFeedback type="warning" inline={true} message={`Nav Items: ${navBinding.dropped.length} bound link(s) skipped - ${navBinding.dropped.join("; ")}`} /> : null}</header>;
}
function _temp(item) {
  return isCompactChromeNavHref(item.href);
}